/* eslint-disable @typescript-eslint/no-explicit-any */
import type { ReactNode } from "react";
import React, { Component } from "react";
import { useTranslation } from "react-i18next";
import { logger } from "@/lib/logger";
import { Button } from "@/components/shared/ui/button";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

// Helper component to use useTranslation hook inside class component
const ErrorDisplay = () => {
  const { t } = useTranslation();

  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <h2 className="text-xl font-semibold mb-2">
          {t("errorBoundary.title")}
        </h2>
        <p className="text-muted-foreground mb-4">
          {t("errorBoundary.description")}
        </p>
        <Button onClick={() => window.location.reload()} size="sm">
          {t("errorBoundary.refresh")}
        </Button>
      </div>
    </div>
  );
};

export class RealtimeErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Check if this is a WebSocket/realtime related error
    if (
      error.message?.includes("WebSocket") ||
      error.message?.includes("insecure") ||
      error.name === "SecurityError"
    ) {
      logger.warn(
        "Realtime connection failed, continuing without realtime features:",
        error
      );
      // Don't treat realtime failures as fatal errors
      return { hasError: false };
    }

    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    if (
      error.message?.includes("WebSocket") ||
      error.message?.includes("insecure") ||
      error.name === "SecurityError"
    ) {
      logger.warn("Caught realtime error, app will continue normally:", error);
      return;
    }

    logger.error("Unexpected error caught by boundary:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <ErrorDisplay />;
    }

    return this.props.children;
  }
}
